import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { MotionConfig } from 'framer-motion';
import { LangProvider } from './i18n';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';

// Everything but the home page loads on demand, so the first visit
// downloads only what it shows.
const Work = lazy(() => import('./pages/Work'));
const CaseStudy = lazy(() => import('./pages/CaseStudy'));
const Services = lazy(() => import('./pages/Services'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/Legal').then((m) => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/Legal').then((m) => ({ default: m.TermsOfService })));
const NotFound = lazy(() => import('./pages/NotFound'));

function Layout({ lang }) {
  // Set before paint-dependent code runs; Helmet also keeps these in sync.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  return (
    <LangProvider lang={lang}>
      <Header />
      <main id="main">
        {/* Holds the page height while a page's code loads, so the footer doesn't jump up. */}
        <Suspense fallback={<div className="min-h-screen" />}>
          <Outlet />
        </Suspense>
      </main>
      <Footer />
    </LangProvider>
  );
}

const pages = (
  <>
    <Route index element={<Home />} />
    <Route path="work" element={<Work />} />
    <Route path="work/:slug" element={<CaseStudy />} />
    <Route path="services" element={<Services />} />
    <Route path="about" element={<About />} />
    <Route path="contact" element={<Contact />} />
    <Route path="privacy-policy" element={<PrivacyPolicy />} />
    <Route path="terms-of-service" element={<TermsOfService />} />
    <Route path="portfolio" element={<Navigate to="../work" replace />} />
    <Route path="*" element={<NotFound />} />
  </>
);

export default function App() {
  return (
    <MotionConfig reducedMotion="user">
      <Router>
        <ScrollToTop />
        <Routes>
          <Route path="/ar" element={<Layout lang="ar" />}>
            {pages}
          </Route>
          <Route path="/" element={<Layout lang="en" />}>
            {pages}
          </Route>
        </Routes>
      </Router>
    </MotionConfig>
  );
}
